/**
 * React-query hooks for the 6sense account Audience Builder (mb-frontend#3391).
 */

import { useQuery } from '@tanstack/react-query';
import {
  getSixsenseAudienceAuthStatus,
  getSixsenseFields,
  SixsenseField,
} from '@/services/sixsenseAudience';

/**
 * Canonical gate for the 6sense audience source card. Distinct from the enrichment
 * integration's useGetSixsenseAuthStatus (different endpoint + query key).
 */
export const useSixsenseAudienceAuthStatus = ({ enabled = true }: { enabled?: boolean } = {}) => useQuery({
  queryKey: ['sixsense-audience-auth-status'],
  queryFn: getSixsenseAudienceAuthStatus,
  enabled,
  staleTime: 1000 * 60 * 5,
  retry: (failureCount: number, error: unknown) => {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 401 || status === 403 || (status && status >= 500)) return false;
    return failureCount < 2;
  },
});

/**
 * Capability-aware field catalog for the current workspace (availability derived from
 * live projection data server-side).
 */
export const useSixsenseFields = ({ enabled = true }: { enabled?: boolean } = {}) => useQuery<{ fields: SixsenseField[]; capability_summary: { total: number; available: number } }>({
  queryKey: ['sixsense-audience-fields'],
  queryFn: async () => {
    const res = await getSixsenseFields();
    return { fields: res.fields, capability_summary: res.capability_summary };
  },
  enabled,
  staleTime: 1000 * 60 * 10,
});
